import { useEffect, useRef, useSyncExternalStore } from "react";
import { useSegmentTrack } from "@/hooks/use-segment";
import { ipcRpcCollector, iframePerfReportCollector } from "@/vscode-ipc";

const REPORT_INTERVAL_MS = 30_000;
const LOCALSTORAGE_KEY = "gitpod_vscode_perf_instrumentation";

/**
 * Reads the perf instrumentation opt-in from localStorage.
 * Enable via: `localStorage.setItem('gitpod_vscode_perf_instrumentation', 'true')`
 * Disable via: `localStorage.removeItem('gitpod_vscode_perf_instrumentation')`
 */
export function isVscodePerfInstrumentationEnabled(): boolean {
    try {
        return localStorage.getItem(LOCALSTORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

function subscribeToStorage(callback: () => void) {
    const handler = (e: StorageEvent) => {
        if (e.key === LOCALSTORAGE_KEY) callback();
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
}

function getSnapshot(): boolean {
    return isVscodePerfInstrumentationEnabled();
}

/** React hook that reactively reads the localStorage toggle. */
export function useVscodePerfInstrumentationEnabled(): boolean {
    return useSyncExternalStore(subscribeToStorage, getSnapshot);
}

/**
 * Forwards VSCode iframe performance data to Segment.
 *
 * The actual observers (Long Task, Event Timing, frame rate, memory, layout
 * shifts) run inside the VSCode iframe (`iframePerfObserver.ts`) where they
 * can see VSCode core's own activity. They send aggregated reports to the
 * dashboard via `window.parent.postMessage`. This hook drains those reports
 * plus the dashboard-side IPC RPC collector, then forwards to Segment.
 *
 * @param enabled - localStorage toggle value. When false, no reporting runs.
 */
export function usePerfInstrumentation(enabled: boolean) {
    const segmentTrack = useSegmentTrack();
    const intervalRef = useRef<ReturnType<typeof setInterval>>();

    useEffect(() => {
        if (!enabled) return;

        intervalRef.current = setInterval(() => {
            // ── Iframe perf reports (from VSCode iframe observers) ───────
            const iframeReports = iframePerfReportCollector.drain();
            for (const report of iframeReports) {
                const role = (report.iframeRole as string) || "unknown";
                if (report.longTasks) {
                    segmentTrack("vscode_perf_iframe_long_tasks", { ...(report.longTasks as Record<string, unknown>), iframeRole: role });
                }
                if (report.eventTiming) {
                    segmentTrack("vscode_perf_iframe_event_timing", { ...(report.eventTiming as Record<string, unknown>), iframeRole: role });
                }
                if (report.layoutShifts) {
                    segmentTrack("vscode_perf_iframe_layout_shifts", { ...(report.layoutShifts as Record<string, unknown>), iframeRole: role });
                }
                if (report.frameRate) {
                    segmentTrack("vscode_perf_iframe_frame_rate", {
                        ...(report.frameRate as Record<string, unknown>),
                        period: REPORT_INTERVAL_MS,
                        iframeRole: role,
                    });
                }
                if (report.memory) {
                    segmentTrack("vscode_perf_iframe_memory", { ...(report.memory as Record<string, unknown>), iframeRole: role });
                }
            }

            // ── Dashboard-side IPC RPC latency ──────────────────────────
            const rpcEntries = ipcRpcCollector.drain();
            if (rpcEntries.length > 0) {
                const slowCalls = rpcEntries.filter((e) => e.maxDuration > 50);
                segmentTrack("vscode_perf_ipc_rpc", {
                    side: "dashboard",
                    slowCallCount: slowCalls.length,
                    calls: rpcEntries.map((e) => ({
                        service: e.service,
                        method: e.method,
                        avgDuration: Math.round(e.totalDuration / e.count),
                        maxDuration: Math.round(e.maxDuration),
                        count: e.count,
                    })),
                });
            }
        }, REPORT_INTERVAL_MS);

        return () => {
            clearInterval(intervalRef.current);
        };
    }, [enabled, segmentTrack]);
}
